'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  User,
  Lock,
  RefreshCw,
  Database,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Save,
  RotateCcw,
} from 'lucide-react';
import { resetPortfolioAsync, loadPortfolioAsync } from '@/app/paper-trading/utils/paperTradingStorage';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface ProfileData {
  displayName: string;
  email: string;
  tradingStyle: string;
  timezone: string;
}

interface SyncPrefs {
  autoSyncTrades: boolean;
  autoSyncBacktests: boolean;
  autoSyncPortfolio: boolean;
  syncInterval: string;
}

type AlertType = { type: 'success' | 'error'; message: string } | null;

export default function AccountPage() {
  const [activeSection, setActiveSection] = useState<'profile' | 'password' | 'sync' | 'paper'>('profile');
  const { user } = useAuth();

  // Profile state
  const [profile, setProfile] = useState<ProfileData>({
    displayName: '',
    email: '',
    tradingStyle: 'swing',
    timezone: 'America/New_York',
  });
  const [profileAlert, setProfileAlert] = useState<AlertType>(null);

  // Password state
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, newPass: false, confirm: false });
  const [passwordAlert, setPasswordAlert] = useState<AlertType>(null);

  // Sync prefs state
  const [syncPrefs, setSyncPrefs] = useState<SyncPrefs>({
    autoSyncTrades: true,
    autoSyncBacktests: true,
    autoSyncPortfolio: false,
    syncInterval: '5',
  });
  const [syncAlert, setSyncAlert] = useState<AlertType>(null);

  // Paper trading state
  const [paperCapital, setPaperCapital] = useState(100000);
  const [newCapital, setNewCapital] = useState('100000');
  const [paperAlert, setPaperAlert] = useState<AlertType>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Load profile from Supabase on mount
  useEffect(() => {
    if (user) {
      const supabase = createClient();
      supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setProfile({
              displayName: data.full_name || '',
              email: data.email || user.email || '',
              tradingStyle: data.trading_style || 'swing',
              timezone: data.timezone || 'America/New_York',
            });
          } else {
            setProfile((prev) => ({ ...prev, email: user.email || '' }));
          }
        });
    }
    loadPortfolioAsync().then((portfolio) => {
      setPaperCapital(portfolio.initialCash);
      setNewCapital(String(portfolio.initialCash));
    });
  }, [user]);

  function showAlert(setter: React.Dispatch<React.SetStateAction<AlertType>>, alert: AlertType) {
    setter(alert);
    setTimeout(() => setter(null), 3500);
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!profile.displayName.trim() || !profile.email.trim()) {
      showAlert(setProfileAlert, { type: 'error', message: 'Display name and email are required.' });
      return;
    }
    try {
      if (user) {
        const supabase = createClient();
        await supabase
          .from('user_profiles')
          .upsert({
            id: user.id,
            email: profile.email,
            full_name: profile.displayName,
            trading_style: profile.tradingStyle,
            timezone: profile.timezone,
          }, { onConflict: 'id' });
      }
      showAlert(setProfileAlert, { type: 'success', message: 'Profile updated successfully.' });
    } catch {
      showAlert(setProfileAlert, { type: 'error', message: 'Failed to save profile. Please try again.' });
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!passwords.current) {
      showAlert(setPasswordAlert, { type: 'error', message: 'Please enter your current password.' });
      return;
    }
    if (passwords.newPass.length < 8) {
      showAlert(setPasswordAlert, { type: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }
    if (passwords.newPass !== passwords.confirm) {
      showAlert(setPasswordAlert, { type: 'error', message: 'New passwords do not match.' });
      return;
    }
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.updateUser({ password: passwords.newPass });
      if (error) throw error;
      setPasswords({ current: '', newPass: '', confirm: '' });
      showAlert(setPasswordAlert, { type: 'success', message: 'Password changed successfully.' });
    } catch (err: any) {
      showAlert(setPasswordAlert, { type: 'error', message: err?.message || 'Failed to change password.' });
    }
  }

  function handleSaveSyncPrefs() {
    showAlert(setSyncAlert, { type: 'success', message: 'Sync preferences saved.' });
  }

  async function handleResetPaperTrading() {
    const capital = parseFloat(newCapital);
    if (isNaN(capital) || capital < 1000) {
      showAlert(setPaperAlert, { type: 'error', message: 'Starting capital must be at least $1,000.' });
      return;
    }
    const fresh = await resetPortfolioAsync();
    fresh.virtualCash = capital;
    fresh.initialCash = capital;
    setPaperCapital(capital);
    setShowResetConfirm(false);
    showAlert(setPaperAlert, { type: 'success', message: `Paper trading reset. New capital: $${capital.toLocaleString()}` });
  }

  const sections = [
    { id: 'profile', label: 'Profile Info', icon: <User size={16} /> },
    { id: 'password', label: 'Change Password', icon: <Lock size={16} /> },
    { id: 'sync', label: 'Sync Preferences', icon: <Database size={16} /> },
    { id: 'paper', label: 'Paper Trading', icon: <RefreshCw size={16} /> },
  ] as const;

  return (
    <AppLayout>
      <div className="pt-14 lg:pt-0 min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card px-6 py-5">
          <h1 className="text-xl font-semibold text-foreground">Account</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your profile, security, and trading preferences</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-0 max-w-5xl mx-auto p-6">
          {/* Sidebar nav */}
          <nav className="lg:w-52 flex-shrink-0 mb-6 lg:mb-0 lg:mr-8">
            <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {sections.map((s) => (
                <li key={s.id} className="flex-shrink-0">
                  <button
                    onClick={() => setActiveSection(s.id)}
                    className={`flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${
                      activeSection === s.id
                        ? 'bg-primary/10 text-primary' :'text-foreground/70 hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <span className="flex-shrink-0">{s.icon}</span>
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">

            {/* ── Profile Info ── */}
            {activeSection === 'profile' && (
              <section>
                <SectionHeader title="Profile Information" description="Update your display name, email, and trading preferences." />
                {profileAlert && <AlertBanner alert={profileAlert} />}
                <form onSubmit={handleSaveProfile} className="space-y-5 mt-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Display Name">
                      <input
                        type="text"
                        value={profile.displayName}
                        onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                        className="input-field"
                        placeholder="Your name"
                      />
                    </Field>
                    <Field label="Email Address">
                      <input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        className="input-field"
                        placeholder="you@example.com"
                      />
                    </Field>
                    <Field label="Trading Style">
                      <select
                        value={profile.tradingStyle}
                        onChange={(e) => setProfile({ ...profile, tradingStyle: e.target.value })}
                        className="input-field"
                        aria-label="Trading style"
                      >
                        <option value="scalping">Scalping</option>
                        <option value="day">Day Trading</option>
                        <option value="swing">Swing Trading</option>
                        <option value="position">Position Trading</option>
                        <option value="algo">Algorithmic</option>
                      </select>
                    </Field>
                    <Field label="Timezone">
                      <select
                        value={profile.timezone}
                        onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                        className="input-field"
                        aria-label="Timezone"
                      >
                        <option value="America/New_York">Eastern (ET)</option>
                        <option value="America/Chicago">Central (CT)</option>
                        <option value="America/Denver">Mountain (MT)</option>
                        <option value="America/Los_Angeles">Pacific (PT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="Europe/Berlin">Berlin (CET)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Singapore">Singapore (SGT)</option>
                      </select>
                    </Field>
                  </div>
                  <div className="pt-2">
                    <button type="submit" className="btn-primary">
                      <Save size={15} className="mr-1.5" />
                      Save Profile
                    </button>
                  </div>
                </form>
              </section>
            )}

            {/* ── Change Password ── */}
            {activeSection === 'password' && (
              <section>
                <SectionHeader title="Change Password" description="Use a strong password of at least 8 characters." />
                {passwordAlert && <AlertBanner alert={passwordAlert} />}
                <form onSubmit={handleChangePassword} className="space-y-5 mt-5 max-w-md">
                  <PasswordField
                    label="Current Password"
                    value={passwords.current}
                    show={showPasswords.current}
                    onChange={(v) => setPasswords({ ...passwords, current: v })}
                    onToggle={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  />
                  <PasswordField
                    label="New Password"
                    value={passwords.newPass}
                    show={showPasswords.newPass}
                    onChange={(v) => setPasswords({ ...passwords, newPass: v })}
                    onToggle={() => setShowPasswords({ ...showPasswords, newPass: !showPasswords.newPass })}
                  />
                  <PasswordField
                    label="Confirm New Password"
                    value={passwords.confirm}
                    show={showPasswords.confirm}
                    onChange={(v) => setPasswords({ ...passwords, confirm: v })}
                    onToggle={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  />
                  {passwords.newPass && (
                    <PasswordStrength password={passwords.newPass} />
                  )}
                  <div className="pt-1">
                    <button type="submit" className="btn-primary">
                      <Lock size={15} className="mr-1.5" />
                      Update Password
                    </button>
                  </div>
                </form>
              </section>
            )}

            {/* ── Sync Preferences ── */}
            {activeSection === 'sync' && (
              <section>
                <SectionHeader title="Data Sync Preferences" description="Control how and when your trading data syncs across devices." />
                {syncAlert && <AlertBanner alert={syncAlert} />}
                <div className="mt-5 space-y-4">
                  <div className="bg-card border border-border rounded-xl p-5 space-y-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Auto-Sync Modules</p>
                    <ToggleRow
                      label="Trade Journal"
                      description="Sync trade entries and edits in real time"
                      checked={syncPrefs.autoSyncTrades}
                      onChange={(v) => setSyncPrefs({ ...syncPrefs, autoSyncTrades: v })}
                    />
                    <ToggleRow
                      label="Backtest Results"
                      description="Sync saved backtests and strategy runs"
                      checked={syncPrefs.autoSyncBacktests}
                      onChange={(v) => setSyncPrefs({ ...syncPrefs, autoSyncBacktests: v })}
                    />
                    <ToggleRow
                      label="Paper Trading Portfolio"
                      description="Sync open positions and trade history"
                      checked={syncPrefs.autoSyncPortfolio}
                      onChange={(v) => setSyncPrefs({ ...syncPrefs, autoSyncPortfolio: v })}
                    />
                  </div>

                  <div className="bg-card border border-border rounded-xl p-5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Sync Interval</p>
                    <Field label="Sync every (minutes)">
                      <select
                        value={syncPrefs.syncInterval}
                        onChange={(e) => setSyncPrefs({ ...syncPrefs, syncInterval: e.target.value })}
                        className="input-field max-w-xs"
                        aria-label="Sync interval"
                      >
                        <option value="1">1 minute</option>
                        <option value="5">5 minutes</option>
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="60">1 hour</option>
                      </select>
                    </Field>
                  </div>

                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 flex gap-3">
                    <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Cloud sync requires a connected Supabase backend. Currently using <span className="font-medium text-foreground">local storage</span> only. Preferences are saved and will activate once sync is enabled.
                    </p>
                  </div>

                  <div className="pt-1">
                    <button onClick={handleSaveSyncPrefs} className="btn-primary">
                      <Save size={15} className="mr-1.5" />
                      Save Preferences
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* ── Paper Trading Reset ── */}
            {activeSection === 'paper' && (
              <section>
                <SectionHeader title="Paper Trading Capital" description="Reset your virtual portfolio and set a new starting capital amount." />
                {paperAlert && <AlertBanner alert={paperAlert} />}
                <div className="mt-5 space-y-4">
                  {/* Current stats */}
                  <div className="bg-card border border-border rounded-xl p-5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Current Portfolio</p>
                    <div className="grid grid-cols-2 gap-4">
                      <StatCard label="Starting Capital" value={`$${paperCapital.toLocaleString()}`} />
                      <StatCard label="Status" value="Active" accent />
                    </div>
                  </div>

                  {/* Reset form */}
                  <div className="bg-card border border-border rounded-xl p-5 space-y-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Reset Configuration</p>
                    <Field label="New Starting Capital (USD)">
                      <div className="relative max-w-xs">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <input
                          type="number"
                          min={1000}
                          step={1000}
                          value={newCapital}
                          onChange={(e) => setNewCapital(e.target.value)}
                          className="input-field pl-7"
                          placeholder="100000"
                        />
                      </div>
                    </Field>
                    <div className="flex gap-2 flex-wrap">
                      {[10000, 25000, 50000, 100000, 250000].map((amt) => (
                        <button
                          key={amt}
                          onClick={() => setNewCapital(String(amt))}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                            newCapital === String(amt)
                              ? 'bg-primary/10 border-primary/30 text-primary' :'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                          }`}
                        >
                          ${(amt / 1000).toFixed(0)}K
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 flex gap-3">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Resetting will <span className="font-medium text-foreground">permanently delete</span> all open positions and trade history. This action cannot be undone.
                    </p>
                  </div>

                  {!showResetConfirm ? (
                    <button
                      onClick={() => setShowResetConfirm(true)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 text-red-500 border border-red-500/20 text-sm font-medium hover:bg-red-500/20 transition-all duration-150"
                    >
                      <RotateCcw size={15} />
                      Reset Paper Trading
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 p-4 bg-red-500/5 border border-red-500/30 rounded-xl">
                      <p className="text-sm text-foreground flex-1">Are you sure? All positions and history will be cleared.</p>
                      <button
                        onClick={handleResetPaperTrading}
                        className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                      >
                        Confirm Reset
                      </button>
                      <button
                        onClick={() => setShowResetConfirm(false)}
                        className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .input-field {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
        }
        .input-field:focus {
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.15);
        }
        .btn-primary {
          display: inline-flex;
          align-items: center;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          font-size: 0.875rem;
          font-weight: 500;
          transition: opacity 0.15s;
          cursor: pointer;
        }
        .btn-primary:hover {
          opacity: 0.9;
        }
      `}</style>
    </AppLayout>
  );
}

// ── Sub-components ──

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="pb-4 border-b border-border">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
    </div>
  );
}

function AlertBanner({ alert }: { alert: { type: 'success' | 'error'; message: string } }) {
  const isSuccess = alert.type === 'success';
  return (
    <div className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm mt-4 ${
      isSuccess ? 'bg-green-500/10 text-green-600 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
    }`}>
      {isSuccess ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
      {alert.message}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}

function PasswordField({
  label, value, show, onChange, onToggle,
}: {
  label: string; value: string; show: boolean;
  onChange: (v: string) => void; onToggle: () => void;
}) {
  return (
    <Field label={label}>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-field pr-10"
          placeholder="••••••••"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
    </Field>
  );
}

function PasswordStrength({ password }: { password: string }) {
  const score = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', 'bg-red-500', 'bg-amber-500', 'bg-yellow-400', 'bg-green-500'];

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= score ? colors[score] : 'bg-muted'}`} />
        ))}
      </div>
      {score > 0 && (
        <p className="text-xs text-muted-foreground">Strength: <span className="font-medium text-foreground">{labels[score]}</span></p>
      )}
    </div>
  );
}

function ToggleRow({
  label, description, checked, onChange,
}: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${checked ? 'bg-primary' : 'bg-muted'}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-background rounded-lg p-3 border border-border">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-sm font-semibold ${accent ? 'text-green-500' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}
